import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ExportCorrectionApprovalResponse } from '@reform-lite/contracts';
import { useAuthStore } from '@/auth/auth-store';
import { apiClient } from '@/lib/api-client';

/**
 * VL-400: approve correcting one failed Link receipt export's invalid add
 * method. The server derives everything but `value` and `reason`; a 401
 * `reauth_required` asks for the password (step-up), never a sign-out.
 */
export function useApproveExportCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      locationId: string;
      exportId: string;
      value: number;
      reason: string;
    }): Promise<ExportCorrectionApprovalResponse> => {
      const response = await apiClient.post<ExportCorrectionApprovalResponse>(
        `/connectors/locations/${input.locationId}/link-status/exports/${input.exportId}/correction`,
        { value: input.value, reason: input.reason },
      );
      return response.data;
    },
    // Re-read in every case: after a transport error the request may exist.
    onSettled: (_data, _error, input) =>
      queryClient.invalidateQueries({ queryKey: ['link-status', input.locationId] }),
  });
}

/** VL-400 step-up: confirm the signed-in account's password; keeps the session. */
export function useReauthenticate() {
  return useMutation({
    mutationFn: async (password: string): Promise<void> => {
      const response = await apiClient.post<{ accessToken: string }>('/auth/reauthenticate', {
        password,
      });
      useAuthStore.setState({ accessToken: response.data.accessToken });
    },
  });
}
